// File: pages/inventory/DeviceManagementPage.jsx
"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Card from "../../components/inventory/modules/Card/Card";
import Button from "../../components/inventory/modules/Button/Button";
import Table from "../../components/inventory/modules/table/table";
import Badge from "../../components/inventory/modules/Badge/Badge";
import styles from "./InventoryDashboardPage.module.css";
import devicesService from "../../services/inventory/devices";
import { formatDateOnly } from "../../utils/dateFormatters";

function useDebounced(value, delay = 500) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const showOrNA = (val) => {
  if (val === null || val === undefined || (typeof val === "string" && val.trim() === "")) return "N/A";
  return val;
};

const DeviceManagementPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounced(searchTerm, 500);

  const [selectedFilter, setSelectedFilter] = useState(null); // { id, office, location, location_id }
  const [selectedDevices, setSelectedDevices] = useState([]);

  const [activePage, setActivePage] = useState(1);
  const [deactivatedPage, setDeactivatedPage] = useState(1);

  const [activePaginationLoading, setActivePaginationLoading] = useState(false);
  const [deactivatedPaginationLoading, setDeactivatedPaginationLoading] = useState(false);

  const [activeAccount, setActiveAccount] = useState(() => {
    try {
      const s = localStorage.getItem("inventory_active_account");
      return s ? JSON.parse(s) : null;
    } catch {
      return null;
    }
  });

  const isAdmin = (activeAccount?.role === "Admin") || activeAccount === null;

  useEffect(() => {
    const onStorage = () => {
      try {
        const s = localStorage.getItem("inventory_active_account");
        setActiveAccount(s ? JSON.parse(s) : null);
      } catch {
        setActiveAccount(null);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const [selectedTenant, setSelectedTenant] = useState(null);
  const switchedTenantId = activeAccount && activeAccount.type === "sub" ? activeAccount.id : null;
  const effectiveTenantId = selectedTenant ?? switchedTenantId;

  const { data: warehouseStatsRaw, isLoading: warehouseStatsLoading } = useQuery(
    ["warehouseStats"],
    () => devicesService.getWarehouseStats(),
    {
      enabled: isAdmin,
      staleTime: 30 * 1000,
      onError: (err) => console.error("warehouseStats error", err),
    },
  );

  // On first admin load choose first tenant (existing behaviour)
  useEffect(() => {
    if (!isAdmin) return;
    const tenantsArray = warehouseStatsRaw?.tenants_stats ?? (Array.isArray(warehouseStatsRaw) ? warehouseStatsRaw : null);
    if (!Array.isArray(tenantsArray) || tenantsArray.length === 0) return;
    if (selectedTenant) return;
    const first = tenantsArray[0];
    if (first?.tenant_id) {
      setSelectedTenant(first.tenant_id);
      setSelectedFilter({
        id: first.tenant_id,
        office: first.tenant_name,
        location: "", // will be auto-filled from tenantStats effect if available
        location_id: null,
      });
      setActivePage(1);
      setDeactivatedPage(1);
    }
  }, [isAdmin, warehouseStatsRaw, selectedTenant]);

  const { data: tenantStats, isLoading: tenantStatsLoading } = useQuery(
    ["tenantStats", effectiveTenantId],
    () => devicesService.getTenantDevicesStats(effectiveTenantId),
    {
      enabled: !!effectiveTenantId,
      staleTime: 30 * 1000,
      onError: (err) => console.error("tenantStats error", err),
    },
  );

  // If a tenant is selected and we don't yet have a selected location, auto-pick the first location (if any).
  useEffect(() => {
    if (!tenantStats || !selectedFilter) return;
    // only act if selectedFilter belongs to this tenant id (best-effort) and location_id not already set
    // tenantStats shape may not include tenant_id, so we just guard by selectedFilter existing and location_id empty.
    if (selectedFilter.location_id) return;

    const locs = tenantStats?.devices?.by_location;
    if (Array.isArray(locs) && locs.length > 0) {
      const firstLoc = locs[0];
      setSelectedFilter((prev) => ({
        ...(prev || {}),
        location: firstLoc.location_name ?? firstLoc.location ?? prev?.location ?? "",
        location_id: firstLoc.location_id ?? firstLoc.id ?? null,
      }));
      // reset pages so it fetches with location in effect
      setActivePage(1);
      setDeactivatedPage(1);
    }
  }, [tenantStats, selectedFilter]);

  const selectedLocationId = selectedFilter?.location_id ?? selectedFilter?.id ?? null;
  const locationParamForApi = isAdmin ? null : selectedLocationId;

  const {
    data: activeDevicesData,
    isLoading: activeDevicesLoading,
  } = useQuery(
    ["tenantDevices", effectiveTenantId, "assigned", locationParamForApi, activePage, debouncedSearch],
    async () => {
      if (!effectiveTenantId) {
        return { meta: { total_items: 0, total_pages: 0, current_page: 1, next: null, previous: null }, results: [] };
      }
      return devicesService.getActiveTenantDevices(effectiveTenantId, {
        locationId: locationParamForApi,
        page: activePage,
        search: debouncedSearch,
      });
    },
    {
      enabled: !!effectiveTenantId,
      keepPreviousData: true,
      onError: (err) => console.error("active devices error", err),
    },
  );

  const {
    data: deactivatedDevicesData,
    isLoading: deactivatedDevicesLoading,
  } = useQuery(
    ["tenantDevices", effectiveTenantId, "unlinked", locationParamForApi, deactivatedPage, debouncedSearch],
    async () => {
      if (!effectiveTenantId) {
        return { meta: { total_items: 0, total_pages: 0, current_page: 1, next: null, previous: null }, results: [] };
      }
      return devicesService.getDeactivatedTenantDevices(effectiveTenantId, {
        locationId: locationParamForApi,
        page: deactivatedPage,
        search: debouncedSearch,
      });
    },
    {
      enabled: !!effectiveTenantId,
      keepPreviousData: true,
      onError: (err) => console.error("deactivated devices error", err),
    },
  );

  const adminLoading = isAdmin && warehouseStatsLoading;
  const tenantLoading = !!effectiveTenantId && (tenantStatsLoading || activeDevicesLoading || deactivatedDevicesLoading);
  const anyLoading = adminLoading || tenantLoading;

  useEffect(() => {
    console.debug("Query flags:", {
      isAdmin,
      effectiveTenantId,
      locationParamForApi,
      warehouseStatsLoading,
      tenantStatsLoading,
      activeDevicesLoading,
      deactivatedDevicesLoading,
      adminLoading,
      tenantLoading,
      anyLoading,
    });
  }, [isAdmin, effectiveTenantId, locationParamForApi, warehouseStatsLoading, tenantStatsLoading, activeDevicesLoading, deactivatedDevicesLoading, adminLoading, tenantLoading, anyLoading]);

  const officeDistributionData = useMemo(() => {
    if (isAdmin) {
      const tenants = warehouseStatsRaw?.tenants_stats ?? (Array.isArray(warehouseStatsRaw) ? warehouseStatsRaw : []);
      if (!Array.isArray(tenants) || tenants.length === 0) return [];
      return tenants.map((t) => ({
        id: t.tenant_id,
        office: t.tenant_name,
        location: "",
        totalDevices: t.devices?.total_devices ?? 0,
        active: t.devices?.assigned ?? 0,
        deactivated: t.devices?.unlinked ?? 0,
        ordered: (t.bulk_orders?.total ?? 0) + (t.dropship_orders?.total ?? 0),
        locations: t.locations ?? [],
        location_id: null,
      }));
    }
    if (tenantStats?.devices?.by_location) {
      return tenantStats.devices.by_location.map((loc) => ({
        id: loc.location_id ?? `loc-${loc.location_name ?? "unassigned"}`,
        office: tenantStats?.tenant_name ?? "Tenant",
        location: loc.location_name ?? "Unassigned",
        totalDevices: loc.total ?? 0,
        active: loc.assigned ?? 0,
        deactivated: loc.unlinked ?? 0,
        ordered: (loc.total_bulk_orders ?? 0) + (loc.total_dropship_orders ?? 0),
        locations: [],
        location_id: loc.location_id ?? null,
      }));
    }
    return [];
  }, [isAdmin, warehouseStatsRaw, tenantStats]);

  const activeResults = activeDevicesData?.results ?? (Array.isArray(activeDevicesData) ? activeDevicesData : []);
  const activeMeta = activeDevicesData?.meta ?? null;
  const deactivatedResults = deactivatedDevicesData?.results ?? (Array.isArray(deactivatedDevicesData) ? deactivatedDevicesData : []);
  const deactivatedMeta = deactivatedDevicesData?.meta ?? null;

  const officeDistributionColumns = useMemo(() => {
    const baseColumns = [];
    if (isAdmin) {
      baseColumns.push({ key: "office", label: "Office (Organization)", alignLeft: true });
    } else {
      baseColumns.push({ key: "location", label: "Location", alignLeft: true });
    }
    baseColumns.push(
      { key: "totalDevices", label: "Total Devices", alignLeft: true },
      { key: "active", label: "Active (Assigned)", alignLeft: true },
      { key: "deactivated", label: "Deactivated", alignLeft: true },
      { key: "ordered", label: "Ordered", alignLeft: true },
      {
        key: "actions",
        label: "Actions",
        sorting: false,
        render: (office) => (
          <Button
            variant={selectedFilter?.id === office.id ? "primary" : "secondary"}
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // set selected tenant (office) and initial filter; location will be auto-filled by tenantStats effect
              setSelectedFilter({
                id: office.id,
                office: office.office,
                location: office.location || "",
                location_id: office.location_id ?? null,
              });
              if (isAdmin && office.id) {
                setSelectedTenant(office.id);
              }
              setActivePage(1);
              setDeactivatedPage(1);
            }}
          >
            View Inventory
          </Button>
        ),
      },
    );
    return baseColumns;
  }, [isAdmin, selectedFilter]);

  const deviceStatusBadge = (status) => {
    if (!status) return { label: "N/A", variant: "default" };
    const s = String(status).toLowerCase();
    if (s === "assigned" || s === "available" || s === "active") return { label: "Assigned", variant: "success" };
    if (s === "unlinked" || s === "inactive" || s === "deactivated") return { label: "Deactivated", variant: "error" };
    return { label: showOrNA(status), variant: "default" };
  };

  // Columns use selectedFilter (so sub-account shows the currently selected tenant/location)
  const activeDevicesColumns = [
    {
      key: "sub_account",
      label: "Sub Account",
      alignLeft: true,
      render: (r) =>
        // prefer selected location if present, then selected office, then server fields
        showOrNA(selectedFilter?.location || selectedFilter?.office || r?.sub_account_name || r?.tenant_name || r?.organization || ""),
    },
    { key: "patient_name", label: "Patient Name", alignLeft: true, render: (r) => showOrNA(r?.patient_name || r?.patient_id || "") },
    { key: "imei", label: "IMEI", alignLeft: true, render: (r) => showOrNA(r?.imei) },
    {
      key: "device_type",
      label: "Device Type",
      alignLeft: true,
      render: (r) =>
        typeof r?.device_type === "object" ? showOrNA(r.device_type?.name) : showOrNA(r?.name || r?.device_type),
    },
    { key: "added_at", label: "Date Device Added", alignLeft: true, render: (r) => formatDateOnly(r?.added_at) },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (r) => {
        const b = deviceStatusBadge(r?.status);
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
    {
      key: "latest_reading",
      label: "Latest Reading",
      alignLeft: true,
      render: (r) => formatDateOnly(r?.latest_reading_at || r?.updated_at || null),
    },
  ];

  const deactivatedDevicesColumns = [
    {
      key: "sub_account",
      label: "Sub Account",
      alignLeft: true,
      render: (r) => showOrNA(selectedFilter?.location || selectedFilter?.office || r?.sub_account_name || r?.tenant_name || r?.organization || ""),
    },
    { key: "patient_name", label: "Patient Name", alignLeft: true, render: (r) => showOrNA(r?.patient_name || r?.patient_id || "") },
    { key: "imei", label: "IMEI", alignLeft: true, render: (r) => showOrNA(r?.imei) },
    {
      key: "device_type",
      label: "Device Type",
      alignLeft: true,
      render: (r) =>
        typeof r?.device_type === "object" ? showOrNA(r.device_type?.name) : showOrNA(r?.name || r?.device_type),
    },
    { key: "date_deactivated", label: "Date Deactivated", alignLeft: true, render: (r) => formatDateOnly(r?.added_at || r?.updated_at) },
    { key: "reason", label: "Reason", alignLeft: true, render: (r) => showOrNA(r?.unlinked_reason || r?.reason || "") },
    {
      key: "status",
      label: "Status",
      alignLeft: true,
      render: (r) => {
        const b = deviceStatusBadge(r?.status);
        return <Badge variant={b.variant}>{b.label}</Badge>;
      },
    },
  ];

  const handleSelectionChange = (selectedIds) => {
    setSelectedDevices(selectedIds);
  };

  const handleActivePageChange = useCallback(
    (which) => {
      setActivePaginationLoading(true);
      if (typeof which === "string" && which.startsWith("http")) {
        setActivePage(which);
      } else {
        setActivePage(Number(which) || 1);
      }
      setTimeout(() => setActivePaginationLoading(false), 800);
    },
    [setActivePage],
  );

  const handleDeactivatedPageChange = useCallback(
    (which) => {
      setDeactivatedPaginationLoading(true);
      if (typeof which === "string" && which.startsWith("http")) {
        setDeactivatedPage(which);
      } else {
        setDeactivatedPage(Number(which) || 1);
      }
      setTimeout(() => setDeactivatedPaginationLoading(false), 800);
    },
    [setDeactivatedPage],
  );

  useEffect(() => {
    setActivePage(1);
    setDeactivatedPage(1);
  }, [debouncedSearch, selectedLocationId, effectiveTenantId]);

  const officeRowColor = useCallback(
    (row) => {
      const rowId = row?.id ?? row?.tenant_id ?? row?.location_id;
      if (selectedFilter?.id && rowId && String(rowId) === String(selectedFilter.id)) {
        return "#eef6ff";
      }
      return "transparent";
    },
    [selectedFilter],
  );

  const deviceRowColor = useCallback(
    (row) => {
      const id = row?.id ?? row?.original?.id ?? row?.serial ?? row?.imei;
      if (id && Array.isArray(selectedDevices) && selectedDevices.some((s) => String(s) === String(id))) {
        return "#eef6ff";
      }
      return "transparent";
    },
    [selectedDevices],
  );

  return (
    <div className={styles.device_management_page}>
      <div className={styles.page_header}>
        <div className={styles.header_left}>
          <h1 className={styles.page_title}>Device Distribution</h1>
          <p className={styles.page_subtitle}>Monitor and distribute devices across inventory across locations</p>
        </div>
      </div>

      <div className={styles.section}>
        <Card title="Office Distribution" className={styles.inventory_card}>
          <Table
            columns={officeDistributionColumns}
            data={officeDistributionData}
            height={300}
            isRowClickable={false}
            loading={isAdmin ? warehouseStatsLoading : tenantStatsLoading}
            rowColor={officeRowColor}
          />
        </Card>
      </div>

      <div className={styles.section}>
        <Card title="Active Devices" className={styles.supplies_card}>
          <Table
            columns={activeDevicesColumns}
            data={activeResults}
            meta={activeMeta}
            pagination={true}
            paginationLoading={activePaginationLoading}
            onPageChange={handleActivePageChange}
            height={400}
            isRowClickable={false}
            loading={activeDevicesLoading}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedDevices}
            rowColor={deviceRowColor}
          />
        </Card>
      </div>

      <div className={styles.section}>
        <Card title="Deactivated Devices" className={styles.supplies_card}>
          <Table
            columns={deactivatedDevicesColumns}
            data={deactivatedResults}
            meta={deactivatedMeta}
            pagination={true}
            paginationLoading={deactivatedPaginationLoading}
            onPageChange={handleDeactivatedPageChange}
            height={400}
            isRowClickable={false}
            loading={deactivatedDevicesLoading}
            onSelectionChange={handleSelectionChange}
            selectedIds={selectedDevices}
            rowColor={deviceRowColor}
          />
        </Card>
      </div>
    </div>
  );
};

export default DeviceManagementPage;
